<script setup lang="ts">
  import { computed } from 'vue';
  import { useModal } from 'vue-final-modal';
  import NotificationTableRow from './NotificationTableRow.vue';
  import { TableHeaderSection, DefaultTable, SearchNoResults, NoDocuments } from '@/shared/ui';
  import NoFilesIcon from '@/shared/assets/images/no-files.svg';
  import ViewNotificationModal from './ViewNotificationModal.vue';
  import { OrderOptions } from '@/shared/lib';
  import { useTableEvents, type NotificationTableEmits, NotificationResponse } from '@/shared/lib';
  import { useNotificationSelection } from '../model/useNotificationSelection';
  import { useNotificationDelete } from '../model/useNotificationDelete';

  const props = defineProps<{
    notifications: NotificationResponse[];
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    orderOptions: OrderOptions[];
    headers: string[];
    isFilterActive?: boolean;
    searchTerm?: string;
    loading?: boolean;
  }>();

  const emit = defineEmits<NotificationTableEmits>();
  const { handleFilter } = useTableEvents(emit as any);

  const handleFilterClicked = () => {
    emit('filter-clicked');
  };

  const hasNotifications = computed(() => props.notifications && props.notifications.length > 0);
  const shouldShowEmptyState = computed(
    () => !hasNotifications.value && !props.searchTerm && !props.isFilterActive
  );

  const shouldShowTable = computed(
    () => hasNotifications.value || props.isFilterActive || !!props.searchTerm || props.loading
  );

  const shouldShowEmptyInTable = computed(
    () => !hasNotifications.value && (props.isFilterActive || !!props.searchTerm)
  );

  const {
    isSelected,
    toggleSelection,
    toggleAll,
    selectedCount,
    hasSelectedNotifications,
    selectedNotificationsArray,
    allSelected,
    removeFromSelection,
  } = useNotificationSelection(
    () => props.notifications,
    (selectedIds) => emit('update:selected-notifications', selectedIds)
  );

  const { handleSingleDelete, openDeleteConfirmationModal, showNoSelectionError } =
    useNotificationDelete(
      () => selectedCount.value,
      () => selectedNotificationsArray.value,
      (deletedIds) => emit('notification-deleted', deletedIds),
      removeFromSelection
    );

  const handleSortOptions = (field: string, order: string) => {
    emit('update:sort', field, order);
  };

  const handleDeleteClick = () => {
    if (hasSelectedNotifications.value) {
      openDeleteConfirmationModal();
    } else {
      showNoSelectionError();
    }
  };

  const handleNotificationAction = async (action: string, notificationId: number) => {
    switch (action) {
      case 'view': {
        const notification = props.notifications.find((n) => n.id === notificationId);
        if (!notification) return;

        const { open: openViewNotificationModal, close: closeViewNotificationModal } = useModal({
          component: ViewNotificationModal,
          attrs: {
            notification,
            onCloseModal() {
              closeViewNotificationModal();
            },
          },
        });

        openViewNotificationModal();
        break;
      }
      case 'edit': {
        emit('notification-edit', notificationId);
        break;
      }
      case 'delete': {
        await handleSingleDelete(notificationId);
        break;
      }
      default: {
        // No default action
        break;
      }
    }
  };
</script>

<template>
  <div class="w-full">
    <DefaultTable
      v-if="shouldShowTable"
      class="mt-4"
    >
      <TableHeaderSection
        :headers="headers"
        :item-count="props.notifications.length"
        :show-item-count="false"
        :show-column-headers="hasNotifications"
        item-type="notificações"
        :has-selected="hasSelectedNotifications"
        :selected-count="selectedCount"
        :selected-documents="selectedNotificationsArray"
        :all-selected="allSelected"
        :order-options="orderOptions"
        :is-filter-active="props.isFilterActive"
        :th-class="
          (header, index) =>
            'text-left text-xs font-semibold text-gray-07 py-4.5 w-min whitespace-nowrap align-middle' +
            (index === 0 ? ' pl-2' : ' pl-6')
        "
        th-checkbox-class="align-middle text-center pl-6 w-15"
        @toggle-all="toggleAll"
        @update:sort="handleSortOptions"
        @update:filter="handleFilter"
        @filter-clicked="handleFilterClicked"
        @delete="handleDeleteClick"
      />
      <tbody class="bg-white-01 divide-y divide-gray-200">
        <NotificationTableRow
          v-for="notification in notifications"
          :key="notification.id"
          :notification="notification"
          :is-selected="isSelected(notification.id)"
          :headers="headers"
          @action="handleNotificationAction"
          @toggle-selection="toggleSelection"
        />

        <tr v-if="shouldShowEmptyInTable">
          <td :colspan="headers.length + 1">
            <SearchNoResults />
          </td>
        </tr>
      </tbody>
    </DefaultTable>

    <NoDocuments
      v-else-if="shouldShowEmptyState"
      title="Ops! Parece que você ainda não contém notificações criadas"
      :icon="NoFilesIcon"
    />
  </div>
</template>
